import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col lg:flex-row">
      {/* Left Panel - Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#007AFF] text-white flex-col justify-between p-12">
        <div>
          <div className="flex items-center">
            <span className="text-3xl brand-name-white">
              Zelvrix
            </span>
          </div>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-bold leading-tight">
            Record meetings.<br />
            Generate follow-ups.<br />
            Close deals faster.
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            The smart CRM for sales reps who want AI-powered task management and automated follow-up generation.
          </p>
        </div>
        
        <div className="text-white/50 text-sm">
          &copy; 2026 Zelvrix. All rights reserved.
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-10 sm:p-8">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-entrance">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-4 sm:mb-8">
            <span className="text-2xl brand-name">
              Zelvrix
            </span>
          </div>
          
          <div className="text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F]">
              Welcome back
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded-xl" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1D1D1F] font-medium text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
                inputMode="email"
                className="input-base h-12 rounded-xl text-base"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#1D1D1F] font-medium text-sm">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#007AFF] font-medium hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="input-base h-12 rounded-xl pr-12 text-base"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 rounded-xl text-base"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              {!loading && <ArrowRight size={20} className="ml-2" />}
            </Button>
          </form>

          <p className="text-center text-sm sm:text-base text-[#86868B]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#007AFF] font-medium hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
